import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	type CreateReportInput,
	CreateReportInputSchema,
	type LightningReportData,
	type Report,
	type ReportDownloadInput,
	type ReportListItem,
	type ReportType,
} from '@xweather/protect-types'
import { HTTPError } from 'ky'
import { z } from 'zod'
import { asQueryParam, type Impersonation, useImpersonation } from '../impersonation.ts'
import { AnalyzeApi, getAllPages } from './api.ts'
import { type AssetShape, toAnalyzeV2Request } from './assetVrns.ts'

export type ReportListItemOf<T extends ReportType> = AssetShape<Extract<ReportListItem, { reportType: T }>>

export const listAnalyzeReports = <T extends ReportType>(type: T, impersonation: Impersonation | undefined) =>
	getAllPages<ReportListItemOf<T>>('api/v2/analyze/reports', { type, ...asQueryParam(impersonation) })

export const getAnalyzeReport = (id: string, impersonation: Impersonation | undefined) =>
	AnalyzeApi.get(`api/v2/analyze/reports/${encodeURIComponent(id)}`, { searchParams: asQueryParam(impersonation) }).json<
		AssetShape<Report>
	>()

export const getAnalyzeReportData = (id: string, impersonation: Impersonation | undefined) =>
	AnalyzeApi.get(`api/v2/analyze/reports/${encodeURIComponent(id)}/data`, {
		searchParams: asQueryParam(impersonation),
	}).json<LightningReportData>()

export const analyzeReportsQuery = <T extends ReportType>(type: T, impersonation: Impersonation | undefined) =>
	queryOptions({
		queryKey: ['ANALYZE_REPORTS', type, impersonation?.subscriptionId],
		refetchInterval: (query) => 1000 * (query.state.data?.some((report) => report.status === 'PENDING') ? 15 : 60),
		staleTime: 1000 * 60,
		queryFn: () => listAnalyzeReports(type, impersonation),
	})

export const useAnalyzeReports = <T extends ReportType>(type: T, { enabled }: { enabled: boolean }) => {
	const impersonation = useImpersonation()
	return useQuery({ ...analyzeReportsQuery(type, impersonation), enabled })
}

const analyzeReportQuery = (id: string, impersonation: Impersonation | undefined) =>
	queryOptions({
		queryKey: ['ANALYZE_REPORT', id, impersonation?.subscriptionId],
		staleTime: 1000 * 60,
		queryFn: () => getAnalyzeReport(id, impersonation),
	})

export const useAnalyzeReport = (id: string) => {
	const impersonation = useImpersonation()
	return useQuery(analyzeReportQuery(id, impersonation))
}

export const createReportDownloadUrl = async (id: string, input: ReportDownloadInput, impersonation: Impersonation | undefined) => {
	const { url } = await AnalyzeApi.post(`api/v2/analyze/reports/${encodeURIComponent(id)}/downloads`, {
		json: input,
		searchParams: asQueryParam(impersonation),
		timeout: 30_000,
	}).json<{ url: string }>()
	return url
}

const ErrorBodySchema = z.object({ error: z.object({ type: z.string(), message: z.string().optional() }) })

export class CreateAnalyzeReportError extends Error {
	readonly detail: string | undefined

	constructor({ code, message }: { code?: string; message?: string }) {
		const detail = [message, code && `(${code})`].filter(Boolean).join(' ') || undefined
		super(detail ?? 'Creating the report failed')
		this.name = 'CreateAnalyzeReportError'
		this.detail = detail
	}
}

const errorBody = async (error: unknown): Promise<{ code?: string; message?: string }> => {
	if (!(error instanceof HTTPError)) return {}

	const body: unknown = await error.response.json().catch(() => undefined)
	const parsed = ErrorBodySchema.safeParse(body).data?.error
	return { code: parsed?.type, message: parsed?.message }
}

export const postCreateInput = async <T>(path: string, body: object, impersonation: Impersonation | undefined): Promise<T> => {
	try {
		return await AnalyzeApi.post(path, { json: body, searchParams: asQueryParam(impersonation) }).json<T>()
	} catch (error) {
		throw new CreateAnalyzeReportError(await errorBody(error))
	}
}

export const createAnalyzeReport = (input: CreateReportInput, impersonation: Impersonation | undefined) =>
	postCreateInput<Report>('api/v2/analyze/reports', toAnalyzeV2Request(CreateReportInputSchema, input), impersonation)

export const useCreateAnalyzeReport = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (input: CreateReportInput) => createAnalyzeReport(input, impersonation),
		onSuccess: (_report, input) => queryClient.invalidateQueries(analyzeReportsQuery(input.reportType, impersonation)),
	})
}
